import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Model display labels ───────────────────────────────────────────────────────
const MODEL_LABELS = {
  'claude-haiku-4-5-20251001':  { short: 'Haiku',    bg: '#2d1b69', tx: '#c084fc', bd: '#7c3aed' },
  'claude-sonnet-4-5-20250929': { short: 'Sonnet',   bg: '#1d3461', tx: '#60a5fa', bd: '#2563eb' },
  'gemini-2.5-flash-lite':      { short: 'G-2.5-Lt', bg: '#14332a', tx: '#4ade80', bd: '#16a34a' },
  'gemini-2.5-flash':           { short: 'G-2.5',    bg: '#14332a', tx: '#22c55e', bd: '#16a34a' },
  'gemini-2.5-pro':             { short: 'G-2.5-Pro',bg: '#14332a', tx: '#34d399', bd: '#059669' },
  'gemini-2.0-flash':           { short: 'G-Flash',  bg: '#14332a', tx: '#4ade80', bd: '#16a34a' },
  'gemini-1.5-pro':             { short: 'G-1.5',    bg: '#14332a', tx: '#34d399', bd: '#059669' },
  'gpt-4o-mini':                { short: 'GPT-mini', bg: '#3d2800', tx: '#fbbf24', bd: '#d97706' },
  'gpt-4o':                     { short: 'GPT-4o',   bg: '#3d2800', tx: '#fb923c', bd: '#ea580c' },
  'gpt-4o-transcribe':          { short: 'GPT-4o-T', bg: '#3d2800', tx: '#f59e0b', bd: '#b45309' },
  'gemini-2.5-flash-audio':     { short: 'G-Audio',  bg: '#14332a', tx: '#86efac', bd: '#15803d' },
  'gemini-2.0-flash-audio':     { short: 'G-Audio',  bg: '#14332a', tx: '#86efac', bd: '#15803d' },
  'whisper-1':                  { short: 'Whisper',  bg: '#042f2e', tx: '#2dd4bf', bd: '#0d9488' },
  'exclusive_trigger':          { short: 'EXCL',     bg: '#1c1c1e', tx: '#9ca3af', bd: '#374151' },
  // Legacy stored labels
  'sonnet':                     { short: 'Sonnet',   bg: '#1d3461', tx: '#60a5fa', bd: '#2563eb' },
  'haiku':                      { short: 'Haiku',    bg: '#2d1b69', tx: '#c084fc', bd: '#7c3aed' },
};

const getModelStyle = (id) => MODEL_LABELS[id] || { short: id?.split('-')[0] || '?', bg: '#1c1c1e', tx: '#9ca3af', bd: '#374151' };

const MODEL_OPTIONS = {
  routing: [
    { value: 'gemini-2.5-flash-lite',       label: 'Gemini 2.5 Flash Lite (default — cheapest)' },
    { value: 'gemini-2.5-flash',            label: 'Gemini 2.5 Flash' },
    { value: 'claude-haiku-4-5-20251001',   label: 'Claude Haiku 4.5' },
    { value: 'claude-sonnet-4-5-20250929',  label: 'Claude Sonnet 4.5 (overkill for routing)' },
    { value: 'gpt-4o-mini',                 label: 'GPT-4o Mini' },
    { value: 'gpt-4o',                      label: 'GPT-4o' },
  ],
  generation: [
    { value: 'gemini-2.5-flash-lite',       label: 'Gemini 2.5 Flash Lite (default — cheapest)' },
    { value: 'gemini-2.5-flash',            label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-pro',              label: 'Gemini 2.5 Pro' },
    { value: 'claude-haiku-4-5-20251001',   label: 'Claude Haiku 4.5' },
    { value: 'claude-sonnet-4-5-20250929',  label: 'Claude Sonnet 4.5 (quality safety net)' },
    { value: 'gpt-4o-mini',                 label: 'GPT-4o Mini' },
    { value: 'gpt-4o',                      label: 'GPT-4o' },
  ],
  voice: [
    { value: 'whisper-1',                   label: 'Whisper-1 (default)' },
    { value: 'gpt-4o-transcribe',           label: 'GPT-4o Transcribe' },
    { value: 'gemini-2.5-flash-audio',      label: 'Gemini 2.5 Flash Audio' },
  ],
};

const DEFAULT_MODEL_CONFIG = {
  routing_model:    'gemini-2.5-flash-lite',
  generation_model: 'gemini-2.5-flash-lite',
  voice_model:      'whisper-1',
};

// ── Cluster colour map (13 clusters) ─────────────────────────────────────────
const CC = {
  1:  { bg: '#1d3461', tx: '#60a5fa', border: '#2563eb' },
  2:  { bg: '#2d1b69', tx: '#c084fc', border: '#7c3aed' },
  3:  { bg: '#431407', tx: '#fb923c', border: '#ea580c' },
  4:  { bg: '#14332a', tx: '#4ade80', border: '#16a34a' },
  5:  { bg: '#3b0764', tx: '#f472b6', border: '#db2777' },
  6:  { bg: '#042f2e', tx: '#2dd4bf', border: '#0d9488' },
  7:  { bg: '#450a0a', tx: '#f87171', border: '#dc2626' },
  8:  { bg: '#3d2800', tx: '#fbbf24', border: '#d97706' },
  9:  { bg: '#083344', tx: '#22d3ee', border: '#0891b2' },
  10: { bg: '#1a2e05', tx: '#a3e635', border: '#65a30d' },
  11: { bg: '#431407', tx: '#fcd34d', border: '#f59e0b' },
  12: { bg: '#1e1b4b', tx: '#818cf8', border: '#4f46e5' },
  13: { bg: '#4c0519', tx: '#fb7185', border: '#e11d48' },
};

// ── Input-type badge (voice / image / sticker / etc.) ───────────────────────
const INPUT_TYPE_STYLES = {
  text:     { icon: '💬', label: 'Text',      bg: '#1a1a1a', tx: '#9ca3af', bd: '#37415130' },
  voice:    { icon: '🎙', label: 'Voice',     bg: '#1e0a3b', tx: '#a855f7', bd: '#a855f730' },
  audio:    { icon: '🔊', label: 'Audio',     bg: '#1e0a3b', tx: '#c084fc', bd: '#a855f730' },
  image:    { icon: '📷', label: 'Image',     bg: '#0d2147', tx: '#3b82f6', bd: '#3b82f630' },
  document: { icon: '📄', label: 'Doc',       bg: '#0a2e1a', tx: '#22c55e', bd: '#22c55e30' },
  video:    { icon: '🎬', label: 'Video',     bg: '#3d2800', tx: '#fb923c', bd: '#ea580c30' },
  sticker:  { icon: '🏷', label: 'Sticker',   bg: '#0c2a40', tx: '#38bdf8', bd: '#38bdf830' },
  reaction: { icon: '😊', label: 'Reaction',  bg: '#3d2800', tx: '#fbbf24', bd: '#d9770630' },
  location: { icon: '📍', label: 'Location',  bg: '#450a0a', tx: '#f87171', bd: '#dc262630' },
};

const getInputTypeStyle = (t) => INPUT_TYPE_STYLES[t] || INPUT_TYPE_STYLES.text;

const InputTypeBadge = ({ type, compact = false }) => {
  const s = getInputTypeStyle(type || 'text');
  return (
    <span
      data-testid={`input-type-badge-${type || 'text'}`}
      style={{ background: s.bg, color: s.tx, border: `1px solid ${s.bd}` }}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono"
      title={`Input: ${s.label}`}
    >
      <span>{s.icon}</span>
      {!compact && <span>{s.label}</span>}
    </span>
  );
};

const getCluster = (id) => {
  if (!id) return 1;
  if (id <= 15) return 1;  if (id <= 29) return 2;  if (id <= 42) return 3;
  if (id <= 52) return 4;  if (id <= 61) return 5;  if (id <= 69) return 6;
  if (id <= 84) return 7;  if (id <= 98) return 8;  if (id <= 110) return 9;
  if (id <= 120) return 10; if (id <= 127) return 11; if (id <= 140) return 12;
  return 13;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTs = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  // Convert to IST (+5:30)
  const ist = new Date(d.getTime() + 5.5 * 3600000);
  return ist.toISOString().replace('T', ' ').slice(0, 19) + ' IST';
};

const maskPhone = (ph) => {
  if (!ph) return '—';
  const digits = ph.replace(/\D/g, '');
  return '••••' + digits.slice(-4);
};

const msColor = (ms, t1, t2) => {
  if (ms == null) return '#6b7280';
  return ms < t1 ? '#34d399' : ms < t2 ? '#fbbf24' : '#f87171';
};

const truncate = (s, n) => (!s ? '—' : s.length > n ? s.slice(0, n) + '…' : s);

const INIT_FILTERS = {
  time: 'today', roles: [], languages: [], routing: [], finalAgents: [],
  channels: [],
  confidence: 'all', haikuTime: 'all', sonnetTime: 'all', totalTime: 'all',
  entry: 'all', userName: '', phone: '', origMsg: '', translation: '',
  haikuIntent: '', sonnetResp: '',
  // Override-only quick chip — when true, hide rows whose routing_method is one
  // of the BASELINE_ROUTING set (confidence_winner / exclusive_trigger / fallback_agent).
  // This lets pilot debugging surface deterministic-refusal, rate-conflict,
  // rule-setter, query-template, etc. in one click.
  overrideOnly: false,
  userFetchTime: 'all', flowCheckTime: 'all', confGateTime: 'all', histFetchTime: 'all',
  onboardingTime: 'all', routeOverheadTime: 'all', haikuConnectTime: 'all',
  telegramPushTime: 'all',
  clusterFetchTime: 'all', agentOverheadTime: 'all',
  sonnetConnectTime: 'all', actionSaveTime: 'all', twilioSendTime: 'all', redisUpdateTime: 'all',
  timingSumTime: 'all', timingGapTime: 'all',
};

// Routing methods that represent the baseline router decision (NOT a deterministic
// override). Used by the Override-only filter chip.
const BASELINE_ROUTING = new Set(['confidence_winner', 'exclusive_trigger', 'fallback_agent']);

const LANGUAGES = ['english','hindi','hinglish','telugu','telugish','tamil','tamilish',
                   'kannada','marathi','gujarati','punjabi','bengali','malayalam','odia','urdu','unknown'];

const ROLE_STYLE = {
  owner:    { bg: '#1d3461', tx: '#60a5fa' },
  employee: { bg: '#431407', tx: '#fb923c' },
  customer: { bg: '#14332a', tx: '#4ade80' },
};

// ── Sub-components ─────────────────────────────────────────────────────────────
// Routing-method label map. ANY routing path that overrides the default
// must be listed here so the Live Log shows the actual path taken, not the
// raw router classification. Unknown methods fall through to UNK so they
// can be spotted and added explicitly (rather than silently masquerading
// as CONF).
export const ROUTING_LABELS = {
  exclusive_trigger:        { label: 'EXCL',   bg: '#2d1b69', tx: '#c084fc', bd: '#7c3aed' },
  confidence_winner:        { label: 'CONF',   bg: '#1d3461', tx: '#60a5fa', bd: '#2563eb' },
  fallback_agent:           { label: 'FALL',   bg: '#1c1c1e', tx: '#9ca3af', bd: '#374151' },
  // Deterministic overrides applied after routing
  deterministic_refusal:    { label: 'REFUSE', bg: '#3b1313', tx: '#fca5a5', bd: '#b91c1c' },
  rule_setter:              { label: 'RULE',   bg: '#1e3a2f', tx: '#6ee7b7', bd: '#047857' },
  deterministic_onboarding: { label: 'WORKER', bg: '#1e293b', tx: '#7dd3fc', bd: '#0369a1' },
  router_correction:        { label: 'CORR',   bg: '#3b2a14', tx: '#fcd34d', bd: '#b45309' },
  correction_target:        { label: 'CORR-T', bg: '#3b2a14', tx: '#fcd34d', bd: '#b45309' },
  query_template:           { label: 'QTPL',   bg: '#1e293b', tx: '#a5b4fc', bd: '#4338ca' },
  pending_missing:          { label: 'PEND',   bg: '#2c1d3b', tx: '#d8b4fe', bd: '#7c3aed' },
  invite_generated:         { label: 'INVITE', bg: '#1e2a3b', tx: '#93c5fd', bd: '#1d4ed8' },
};

const RoutingBadge = ({ method, gated }) => {
  if (gated) return <span data-testid="routing-gate" title="confidence_gate_triggered" style={{ background:'#451a03', color:'#fbbf24', border:'1px solid #92400e' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold">GATE</span>;
  const B = ROUTING_LABELS[method] || { label: method ? 'UNK' : 'CONF', bg: '#1c1c1e', tx: '#9ca3af', bd: '#374151' };
  return <span title={method || 'unknown'} style={{ background: B.bg, color: B.tx, border: `1px solid ${B.bd}` }} className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold">{B.label}</span>;
};

const AgentBadge = ({ id, name, winner }) => {
  const cl = CC[getCluster(id)] || CC[1];
  const lowConf = false;
  return (
    <span title={name || `Agent ${id}`}
          style={{ background: lowConf ? '#450a0a' : cl.bg, color: cl.tx, border: `1px solid ${cl.border}` }}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono">
      #{id}{winner && <span className="text-emerald-400 ml-0.5">✓</span>}
    </span>
  );
};

const ConfBar = ({ val }) => {
  if (val == null) return <span className="text-gray-600 text-xs">—</span>;
  const pct = Math.round(val * 100);
  const col = pct >= 80 ? '#34d399' : pct >= 60 ? '#fbbf24' : '#f87171';
  return (
    <div className="flex items-center gap-1.5 min-w-[70px]">
      <div className="flex-1 h-1 rounded-full" style={{ background:'#374151' }}>
        <div style={{ width:`${pct}%`, background:col }} className="h-full rounded-full"/>
      </div>
      <span style={{ color:col }} className="text-[10px] font-mono w-7 text-right">{pct}%</span>
    </div>
  );
};

const TCell = ({ ms, t1, t2 }) => {
  if (ms == null) return <span className="text-gray-600 text-xs">—</span>;
  const col = msColor(ms, t1, t2);
  return <span style={{ color:col }} className="text-xs font-mono">{ms.toLocaleString()}ms</span>;
};

// ── Timing breakdown column definitions ──────────────────────────────────────
const TIMING_BREAKDOWN_COLS = [
  { key: 'onboardingTime',       field: 'onboarding_ms',        label: 'Onboard',      w: 64,  t1: 200,  t2: 800  },
  { key: 'userFetchTime',        field: 'user_fetch_ms',         label: 'User Fetch',   w: 72,  t1: 100,  t2: 500  },
  { key: 'langDetectTime',       field: 'lang_detect_ms',        label: 'Lang Det',     w: 64,  t1: 20,   t2: 100  },
  { key: 'flowCheckTime',        field: 'pending_flow_ms',       label: 'Flow Chk',     w: 68,  t1: 50,   t2: 200  },
  { key: 'pendingChoiceTime',    field: 'pending_choice_ms',     label: 'Choice Chk',   w: 72,  t1: 50,   t2: 300  },
  { key: 'pendingRuleTime',      field: 'pending_rule_ms',       label: 'Rule Chk',     w: 64,  t1: 50,   t2: 300  },
  { key: 'pendingStateTime',     field: 'pending_state_ms',      label: 'MGET',         w: 56,  t1: 50,   t2: 300  },
  { key: 'correctionCheckTime',  field: 'correction_check_ms',   label: 'Corr Chk',     w: 64,  t1: 5,    t2: 30   },
  { key: 'multiReviewTime',      field: 'multi_review_ms',       label: 'Rev Chk',      w: 60,  t1: 5,    t2: 30   },
  { key: 'pendingMissingTime',   field: 'pending_missing_ms',    label: 'Miss Chk',     w: 60,  t1: 5,    t2: 30   },
  { key: 'contextSwitchTime',    field: 'context_switch_ms',     label: 'Ctx Switch',   w: 72,  t1: 100,  t2: 500  },
  { key: 'creditsCheckTime',     field: 'credits_check_ms',      label: 'Credits',      w: 60,  t1: 50,   t2: 300  },
  { key: 'telegramPushTime',     field: 'telegram_push_ms',      label: 'TG Push',      w: 64,  t1: 300,  t2: 1000 },
  { key: 'transcriptionTime',    field: 'transcription_ms',      label: 'Transcribe',   w: 88,  t1: 1000, t2: 5000 },
  { key: 'histFetchTime',        field: 'history_fetch_ms',      label: 'History',      w: 68,  t1: 100,  t2: 500  },
  { key: 'routeOverheadTime',    field: 'route_overhead_ms',     label: 'Route OH',     w: 68,  t1: 100,  t2: 500  },
  { key: 'haikuConnectTime',     field: 'haiku_connect_ms',      label: 'Routing Conn', w: 84,  t1: 200,  t2: 500  },
  { key: 'multibizRecheckTime',  field: 'multibiz_recheck_ms',   label: 'Biz Recheck',  w: 76,  t1: 50,   t2: 300  },
  { key: 'ruleSetterTime',       field: 'rule_setter_ms',        label: 'Rule Set',     w: 64,  t1: 50,   t2: 500  },
  { key: 'entryRefTime',         field: 'entry_ref_ms',          label: 'Entry Ref',    w: 64,  t1: 50,   t2: 200  },
  { key: 'confGateTime',         field: 'confidence_gate_ms',    label: 'Conf Gate',    w: 72,  t1: 50,   t2: 200  },
  { key: 'clusterFetchTime',     field: 'cluster_fetch_ms',      label: 'Cluster',      w: 68,  t1: 100,  t2: 500  },
  { key: 'agentOverheadTime',    field: 'agent_overhead_ms',     label: 'Agent OH',     w: 68,  t1: 200,  t2: 800  },
  { key: 'sonnetConnectTime',    field: 'sonnet_connect_ms',     label: 'Gen Conn',     w: 72,  t1: 300,  t2: 800  },
  { key: 'choiceSaveTime',       field: 'pending_choice_save_ms', label: 'Choice Save', w: 76,  t1: 50,   t2: 300  },
  { key: 'actionSaveTime',       field: 'action_save_ms',        label: 'Action',       w: 64,  t1: 200,  t2: 500  },
  { key: 'validatorTime',        field: 'validator_ms',          label: 'Validator',    w: 72,  t1: 800,  t2: 2000 },
  { key: 'contextIndicatorTime', field: 'context_indicator_ms',  label: 'Ctx Ind',      w: 60,  t1: 50,   t2: 300  },
  { key: 'twilioSendTime',       field: 'twilio_send_ms',        label: 'Twilio',       w: 64,  t1: 500,  t2: 1500 },
  { key: 'redisUpdateTime',      field: 'redis_update_ms',       label: 'Redis',        w: 60,  t1: 50,   t2: 200  },
  { key: 'timingSumTime',        field: 'timing_sum_ms',         label: 'Sum',          w: 64,  t1: 3000, t2: 7000 },
  { key: 'timingGapTime',        field: 'timing_gap_ms',         label: 'Gap',          w: 60,  t1: 50,   t2: 200  },
];

// ── Row Expansion Modal ────────────────────────────────────────────────────────
const ExpandModal = ({ row, onClose }) => {
  const cl = CC[getCluster(row.agent_routed_final)] || CC[1];
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
         style={{ background:'rgba(0,0,0,0.85)' }} onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
           style={{ background:'#1c1c1e', border:'1px solid #3c3c3e', maxHeight:'90vh', overflowY:'auto' }}
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor:'#3c3c3e' }}>
          <div className="flex items-center gap-2">
            <div style={{ background: row.error_message ? '#450a0a' : '#14332a', color: row.error_message ? '#f87171' : '#4ade80' }}
                 className="w-2 h-2 rounded-full"/>
            <span className="text-sm font-semibold text-white">Pipeline Detail</span>
            <span className="text-xs text-gray-400">{fmtTs(row.created_at)}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">×</button>
        </div>

        {/* Section 1: User Input */}
        <div className="px-5 py-4 border-b" style={{ borderColor:'#2c2c2e' }}>
          <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-3">User Input</div>
          <div className="space-y-2 text-sm">
            <div className="flex gap-3 items-center">
              <span className="text-gray-500 w-28 shrink-0">Input Type</span>
              <InputTypeBadge type={row.input_type}/>
              {row.media_id && (
                <span className="text-[10px] font-mono text-gray-500 truncate" title={row.media_id}>
                  media_id: {String(row.media_id).slice(0, 16)}…
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Original</span>
              <span className="text-white break-words">{row.original_content || '—'}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Translation</span>
              <span className="text-gray-300 break-words">{row.english_translation || '—'}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Language</span>
              <span className="text-blue-300">{row.detected_language || '—'}</span>
            </div>
            {(row.media_transcription || (row.is_voice_note && row.voice_transcription)) && (
              <div className="flex gap-3">
                <span className="text-gray-500 w-28 shrink-0">Voice → Text</span>
                <span className="text-purple-300 break-words" data-testid="voice-transcription">
                  🎙 {row.media_transcription || row.voice_transcription}
                </span>
              </div>
            )}
          </div>
        </div>        {/* Section 2: Routing Model Layer */}
        <div className="px-5 py-4 border-b" style={{ borderColor:'#2c2c2e' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Routing Model</div>
              <ModelBadge modelId={row.router_model_used}/>
            </div>
            <TCell ms={row.response_time_haiku_ms} t1={500} t2={1000}/>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Intent</span>
              <span className="text-blue-200 break-words">{row.haiku_intent_summary || '—'}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Routing</span>
              <RoutingBadge method={row.routing_method} gated={row.confidence_gate_triggered}/>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Gate</span>
              <span className={row.confidence_gate_triggered ? 'text-amber-400' : 'text-gray-500'}>
                {row.confidence_gate_triggered ? 'Triggered — sent clarification' : 'Not triggered'}
              </span>
            </div>
            {(row.router_model_used || row.model_used) && (
              <div className="flex gap-3">
                <span className="text-gray-500 w-28 shrink-0">Models</span>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-600">Routing:</span>
                    <ModelBadge modelId={row.router_model_used}/>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-600">Generation:</span>
                    <ModelBadge modelId={row.model_used}/>
                  </div>
                </div>
              </div>
            )}
            {(row.agents_considered || []).length > 0 && (
              <div className="flex gap-3">
                <span className="text-gray-500 w-28 shrink-0 pt-0.5">Agents evaluated</span>
                <div className="flex flex-col gap-1.5">
                  {(row.agents_considered || []).map((a, i) => {
                    const ac = CC[getCluster(a.agent_id)] || CC[1];
                    const isWinner = a.agent_id === row.agent_routed_final;
                    return (
                      <div key={a.agent_id ?? `agent-pos-${i}`} className="flex items-center gap-2">
                        <span style={{ background:ac.bg, color:ac.tx, border:`1px solid ${ac.border}` }}
                              className="px-1.5 py-0.5 rounded text-[10px] font-mono">
                          #{a.agent_id}
                        </span>
                        <span className="text-gray-400 text-xs">confidence:</span>
                        <ConfBar val={a.confidence}/>
                        {isWinner && <span className="text-emerald-400 text-xs font-mono">✓ WON</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Generation Model Layer */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Generation Model</div>
              <ModelBadge modelId={row.model_used}/>
            </div>
            <TCell ms={row.response_time_sonnet_ms} t1={1000} t2={3000}/>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Response</span>
              <span className="text-emerald-200 break-words whitespace-pre-wrap">{row.sonnet_response || '—'}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-500 w-28 shrink-0">Agent</span>
              {row.agent_routed_final
                ? <span style={{ background:cl.bg, color:cl.tx, border:`1px solid ${cl.border}` }}
                        className="px-2 py-0.5 rounded text-xs font-mono">
                    #{row.agent_routed_final} {row.agent_routed_name}
                  </span>
                : <span className="text-gray-500">—</span>
              }
            </div>
            {row.entry_id && (
              <div className="flex gap-3">
                <span className="text-gray-500 w-28 shrink-0">Entry</span>
                <span className="text-yellow-300 font-mono text-xs">{row.bill_number || row.entry_id.slice(0, 8)}</span>
              </div>
            )}
            {row.error_message && (
              <div className="flex gap-3">
                <span className="text-gray-500 w-28 shrink-0">Error</span>
                <span className="text-red-400 text-xs break-words">{row.error_message}</span>
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <span className="text-gray-500 w-28 shrink-0">Total pipeline</span>
              <TCell ms={row.response_time_total_ms} t1={3000} t2={7000}/>
            </div>
          </div>
        </div>

        {/* Section 3b: Fallback Status */}
        <div className="px-5 py-4 border-t" style={{ borderColor:'#2c2c2e' }}>
          <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-3">Fallback Status</div>
          <div className="space-y-2">
            {/* Routing model fallback */}
            <div className="flex items-center gap-3">
              <span className="text-gray-500 text-xs w-28 shrink-0">Routing Model</span>
              {row.router_fallback_triggered
                ? <span style={{ background:'#3d1a00', color:'#fb923c', border:'1px solid #ea580c' }}
                        className="px-2 py-0.5 rounded text-[10px] font-mono">
                    ⚠ Fallback Triggered
                  </span>
                : <span style={{ background:'#14332a', color:'#4ade80', border:'1px solid #16a34a' }}
                        className="px-2 py-0.5 rounded text-[10px] font-mono">
                    ✓ No Fallback
                  </span>
              }
              {row.router_fallback_reason && (
                <span className="text-orange-300 text-[10px] font-mono">{row.router_fallback_reason}</span>
              )}
            </div>
            {/* Generation model fallback */}
            <div className="flex items-center gap-3">
              <span className="text-gray-500 text-xs w-28 shrink-0">Generation Model</span>
              {row.model_fallback_triggered
                ? <span style={{ background:'#450a0a', color:'#f87171', border:'1px solid #dc2626' }}
                        className="px-2 py-0.5 rounded text-[10px] font-mono">
                    ⚠ Fallback Triggered
                  </span>
                : <span style={{ background:'#14332a', color:'#4ade80', border:'1px solid #16a34a' }}
                        className="px-2 py-0.5 rounded text-[10px] font-mono">
                    ✓ No Fallback
                  </span>
              }
              {row.model_fallback_reason && (
                <span className="text-red-300 text-[10px] font-mono break-words">{row.model_fallback_reason}</span>
              )}
            </div>
            {(row.model_fallback_triggered || row.router_fallback_triggered) && (
              <div className="mt-2 rounded-lg p-2.5 text-[10px] text-orange-200 leading-relaxed"
                   style={{ background:'#2d1000', border:'1px solid #92400e' }}>
                A fallback condition was detected. The response sent was <span className="font-mono font-bold">"Model fallback required."</span>
                — no secondary model was used. Switch to a more capable model in the Model Selector to resolve this.
              </div>
            )}
          </div>
        </div>

        {/* Section 4: Pipeline Timing Waterfall */}
        {row.timing_sum_ms != null && (
          <div className="px-5 py-4 border-t" style={{ borderColor:'#2c2c2e' }} data-testid="timing-waterfall">
            <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-3">Pipeline Waterfall</div>
            {(() => {
              const steps = [
                { label: 'Onboard',      ms: row.onboarding_ms,        t1: 200,  t2: 800  },
                { label: 'User Fetch',   ms: row.user_fetch_ms,         t1: 100,  t2: 500  },
                { label: 'Lang Det',     ms: row.lang_detect_ms,        t1: 20,   t2: 100  },
                { label: 'Flow Chk',     ms: row.pending_flow_ms,       t1: 50,   t2: 200  },
                { label: 'Choice Chk',   ms: row.pending_choice_ms,     t1: 50,   t2: 300  },
                { label: 'Rule Chk',     ms: row.pending_rule_ms,       t1: 50,   t2: 300  },
                { label: 'MGET',         ms: row.pending_state_ms,      t1: 50,   t2: 300  },
                { label: 'Corr Chk',     ms: row.correction_check_ms,   t1: 5,    t2: 30   },
                { label: 'Rev Chk',      ms: row.multi_review_ms,       t1: 5,    t2: 30   },
                { label: 'Miss Chk',     ms: row.pending_missing_ms,    t1: 5,    t2: 30   },
                { label: 'Ctx Switch',   ms: row.context_switch_ms,     t1: 100,  t2: 500  },
                { label: 'Credits',      ms: row.credits_check_ms,      t1: 50,   t2: 300  },
                { label: 'History',      ms: row.history_fetch_ms,      t1: 100,  t2: 500  },
                { label: 'Route OH',     ms: row.route_overhead_ms,     t1: 100,  t2: 500  },
                { label: 'Routing Conn', ms: row.haiku_connect_ms,      t1: 200,  t2: 500  },
                { label: 'Routing Gen',  ms: row.response_time_haiku_ms != null && row.haiku_connect_ms != null
                    ? Math.max(0, (row.response_time_haiku_ms||0) - (row.haiku_connect_ms||0)) : null, t1: 300, t2: 700 },
                { label: 'Biz Recheck',  ms: row.multibiz_recheck_ms,   t1: 50,   t2: 300  },
                { label: 'Rule Set',     ms: row.rule_setter_ms,        t1: 50,   t2: 500  },
                { label: 'Entry Ref',    ms: row.entry_ref_ms,          t1: 50,   t2: 200  },
                { label: 'Conf Gate',    ms: row.confidence_gate_ms,    t1: 50,   t2: 200  },
                { label: 'Cluster',      ms: row.cluster_fetch_ms,      t1: 100,  t2: 500  },
                { label: 'Agent OH',     ms: row.agent_overhead_ms,     t1: 200,  t2: 800  },
                { label: 'Gen Conn',     ms: row.sonnet_connect_ms,     t1: 300,  t2: 800  },
                { label: 'Gen Gen',      ms: row.response_time_sonnet_ms != null && row.sonnet_connect_ms != null
                    ? Math.max(0, (row.response_time_sonnet_ms||0) - (row.sonnet_connect_ms||0)) : null, t1: 500, t2: 2000 },
                { label: 'Choice Save',  ms: row.pending_choice_save_ms, t1: 50,  t2: 300  },
                { label: 'Action',       ms: row.action_save_ms,        t1: 200,  t2: 500  },
                { label: 'Validator',    ms: row.validator_ms,          t1: 800,  t2: 2000 },
                { label: 'Ctx Ind',      ms: row.context_indicator_ms,  t1: 50,   t2: 300  },
                { label: 'Twilio',       ms: row.twilio_send_ms,        t1: 500,  t2: 1500 },
                { label: 'Redis',        ms: row.redis_update_ms,       t1: 50,   t2: 200  },
              ].filter(s => s.ms != null && s.ms > 0);
              const maxMs = Math.max(...steps.map(s => s.ms || 0), 1);
              return (
                <div className="space-y-1">
                  {steps.map((s, i) => {
                    const pct = Math.max(2, Math.round((s.ms / maxMs) * 100));
                    const col = msColor(s.ms, s.t1, s.t2);
                    return (
                      <div key={s.label || `step-${i}`} className="flex items-center gap-2 text-[11px]">
                        <span className="text-gray-400 w-24 text-right shrink-0 font-mono">{s.label}</span>
                        <div className="flex-1 h-3.5 rounded-sm overflow-hidden" style={{ background:'#1a1a1a' }}>
                          <div style={{ width:`${pct}%`, background:col, opacity: 0.75 }} className="h-full rounded-sm transition-all"/>
                        </div>
                        <span style={{ color:col }} className="text-[10px] font-mono w-14 text-right">{(s.ms||0).toLocaleString()}ms</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 text-[11px] pt-1.5 mt-1 border-t" style={{ borderColor:'#2c2c2e' }}>
                    <span className="text-gray-500 w-24 text-right shrink-0 font-mono">Sum</span>
                    <span className="text-white font-mono text-xs">{(row.timing_sum_ms || 0).toLocaleString()}ms</span>
                    <span className="text-gray-600 mx-1">|</span>
                    <span className="text-gray-500 font-mono">Gap</span>
                    <span style={{ color: msColor(row.timing_gap_ms, 200, 500) }} className="font-mono text-xs">{(row.timing_gap_ms || 0).toLocaleString()}ms</span>
                    <span className="text-gray-600 mx-1">|</span>
                    <span className="text-gray-500 font-mono">Total</span>
                    <span className="text-white font-mono text-xs">{(row.response_time_total_ms || 0).toLocaleString()}ms</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Session Replay Panel ──────────────────────────────────────────────────────
const ReplayPanel = ({ user, rows, onClose }) => {
  const [entered, setEntered] = useState(false);
  const [expanded, setExpanded] = useState({});
  const chatRef = useRef(null);

  // Slide in on mount
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 10);
    return () => clearTimeout(t);
  }, []);

  // ESC to close
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [onClose]);

  // Auto-scroll to bottom when animation completes
  useEffect(() => {
    if (entered && chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [entered]);

  // Filter turns for this user, oldest first
  const turns = rows
    .filter(r => r.user_id === user.user_id)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const fmtShort = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const ist = new Date(d.getTime() + 5.5 * 3600000);
    return ist.toISOString().slice(11, 16) + ' IST';
  };

  const rs = ROLE_STYLE[user.user_role] || { bg: '#1c1c1e', tx: '#9ca3af' };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[150]" onClick={onClose}
           style={{ background: 'rgba(0,0,0,0.45)' }}/>

      {/* Sliding panel */}
      <div data-testid="replay-panel"
           className="fixed top-0 right-0 bottom-0 z-[160] flex flex-col"
           style={{
             width: 'min(400px, 100vw)',
             background: '#1c1c1e',
             borderLeft: '1px solid #3c3c3e',
             boxShadow: '-12px 0 40px rgba(0,0,0,0.7)',
             transform: entered ? 'translateX(0)' : 'translateX(100%)',
             transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
           }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0"
             style={{ borderBottom: '1px solid #3c3c3e', background: '#141414' }}>
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white text-sm font-semibold truncate">
                {user.user_name || 'Unknown User'}
              </span>
              {user.user_role && user.user_role !== 'unknown' && (
                <span style={{ background: rs.bg, color: rs.tx, border: `1px solid ${rs.tx}30` }}
                      className="px-1.5 py-0.5 rounded text-[10px] font-mono capitalize shrink-0">
                  {user.user_role}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-[11px] font-mono">{maskPhone(user.user_phone)}</span>
              <span className="text-gray-700">·</span>
              <span className="text-gray-500 text-[11px]">
                {turns.length} message{turns.length !== 1 ? 's' : ''}
              </span>
              <span className="text-gray-700">·</span>
              <span className="text-gray-600 text-[10px] font-mono">Session Replay</span>
            </div>
          </div>
          <button data-testid="replay-panel-close"
                  onClick={onClose}
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors text-lg leading-none">
            ×
          </button>
        </div>

        {/* Chat area */}
        <div ref={chatRef}
             className="flex-1 overflow-y-auto px-4 py-4 space-y-5"
             style={{ scrollbarWidth: 'thin', scrollbarColor: '#3c3c3e transparent' }}>

          {turns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <span className="text-gray-600 text-sm">No messages in current view for this user</span>
              <span className="text-gray-700 text-xs">Try switching the date filter to All Time</span>
            </div>
          ) : (
            turns.map((turn, i) => {
              const agentLabel = turn.agent_routed_final
                ? `#${turn.agent_routed_final} ${turn.agent_routed_name || ''}`.trim()
                : null;
              const cl = CC[getCluster(turn.agent_routed_final)] || CC[4];
              const respText = turn.sonnet_response || '';
              const isLong = respText.length > 300;
              const isExp = !!expanded[i];
              const displayText = isLong && !isExp ? respText.slice(0, 300) + '…' : respText;

              return (
                <div key={turn.id || i} className="flex flex-col gap-2.5">

                  {/* User bubble — left */}
                  {turn.original_content && (
                    <div style={{ maxWidth: '75%' }}>
                      <div className="rounded-2xl rounded-tl-sm px-3 py-2 text-xs text-gray-100 break-words"
                           style={{ background: '#2c2c2e', border: '1px solid #3a3a3c' }}>
                        {turn.original_content}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 ml-1.5">
                        <span className="text-[10px] text-gray-600 font-mono">{fmtShort(turn.created_at)}</span>
                        {turn.detected_language && (
                          <span className="text-[10px] text-gray-700 capitalize">· {turn.detected_language}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bot bubble — right */}
                  {turn.sonnet_response && (
                    <div className="flex flex-col items-end ml-auto" style={{ maxWidth: '88%' }}>
                      <div className="rounded-2xl rounded-tr-sm px-3 py-2.5 text-xs break-words whitespace-pre-wrap"
                           style={{ background: '#14332a', border: '1px solid #16a34a', color: '#d1fae5' }}>
                        {displayText}
                        {isLong && (
                          <button onClick={() => setExpanded(ex => ({ ...ex, [i]: !ex[i] }))}
                                  className="block mt-1.5 text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors">
                            {isExp ? 'Show less ↑' : 'Show more ↓'}
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 mr-1.5 flex-wrap justify-end">
                        <span className="text-[10px] text-gray-600 font-mono">{fmtShort(turn.created_at)}</span>
                        {agentLabel && (
                          <span className="text-[10px] font-mono px-1 py-0.5 rounded"
                                style={{ background: cl.bg, color: cl.tx, border: `1px solid ${cl.border}` }}>
                            {agentLabel}
                          </span>
                        )}
                        {turn.entry_id && (
                          <span data-testid={`entry-badge-${i}`}
                                className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                                style={{ background: '#3d2800', color: '#fbbf24', border: '1px solid #92400e' }}>
                            ✓ Entry saved
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

// ── Agent Summary Bar ─────────────────────────────────────────────────────────
const AgentSummaryBar = ({ stats, timingAvg, onAgentFilter }) => (
  <div className="flex flex-col" style={{ background:'#141414', borderBottom:'1px solid #2c2c2e' }}>
    <div className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
      <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest shrink-0">Top Agents</span>
      {stats.top.map(a => {
        const cl = CC[getCluster(a.id)] || CC[1];
        return (
          <button key={a.id} onClick={() => onAgentFilter(a.id)}
                  title={`${a.name} — ${a.cnt} messages`}
                  style={{ background:cl.bg, color:cl.tx, border:`1px solid ${cl.border}` }}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono hover:opacity-80 transition-opacity">
            <span>#{a.id}</span>
            <span className="opacity-70">{a.name?.split(' ').slice(0,2).join(' ')}</span>
            <span className="font-bold">{a.pct}%</span>
          </button>
        );
      })}
      {stats.rest > 0 && <span className="text-gray-500 text-[11px]">+{stats.rest} more</span>}
      <div className="ml-auto flex items-center gap-4 text-[11px] text-gray-400 font-mono shrink-0">
        <span>Total: <span className="text-white">{stats.total}</span></span>
        <span>Avg: <span style={{ color: msColor(stats.avgMs, 3000, 7000) }}>{stats.avgMs ? stats.avgMs.toLocaleString() + 'ms' : '—'}</span></span>
        {stats.total > 0 && (
          <span title={`${stats.fallbackCount} fallback${stats.fallbackCount !== 1 ? 's' : ''} in current view`}
                style={{
                  color: stats.fallbackRate === 0 ? '#4ade80' : stats.fallbackRate <= 5 ? '#facc15' : '#f87171',
                  fontWeight: stats.fallbackRate > 0 ? 600 : 400
                }}>
            Fallback: {stats.fallbackRate}%
          </span>
        )}
      </div>
    </div>
    {timingAvg && (
      <div className="flex items-center gap-3 px-4 pb-2 flex-wrap text-[10px] font-mono" data-testid="timing-avg-bar">
        <span className="text-gray-600 uppercase tracking-widest shrink-0">Avg breakdown</span>
        <span className="text-gray-500">Fetch: <span style={{ color: msColor(timingAvg.userFetch, 100, 500) }}>{timingAvg.userFetch}ms</span></span>
        <span className="text-gray-600">|</span>
        <span className="text-gray-500">Routing: <span style={{ color: msColor(timingAvg.haiku, 500, 1000) }}>{timingAvg.haiku}ms</span></span>
        <span className="text-gray-600">|</span>
        <span className="text-gray-500">Generation: <span style={{ color: msColor(timingAvg.sonnet, 1000, 3000) }}>{timingAvg.sonnet}ms</span></span>
        <span className="text-gray-600">|</span>
        <span className="text-gray-500">Twilio: <span style={{ color: msColor(timingAvg.twilio, 500, 1500) }}>{timingAvg.twilio}ms</span></span>
        <span className="text-gray-600">|</span>
        <span className="text-gray-500">Other: <span style={{ color: msColor(timingAvg.other, 200, 500) }}>{timingAvg.other}ms</span></span>
      </div>
    )}
  </div>
);

// ── Column filter dropdown ─────────────────────────────────────────────────────
const ColFilter = ({ col, children, active, openFilter, setOpenFilter }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (openFilter !== col) return;
    const fn = e => { if (!ref.current?.contains(e.target)) setOpenFilter(null); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [openFilter, col, setOpenFilter]);
  return (
    <span ref={ref} className="relative inline-block align-middle ml-1" onClick={e => e.stopPropagation()}>
      <button data-testid={`filter-${col}`}
              onClick={() => setOpenFilter(prev => prev === col ? null : col)}
              className={`text-[10px] ${active ? 'text-blue-400' : 'text-gray-600 hover:text-gray-400'} transition-colors`}>
        {active ? '◆' : '▾'}
      </button>
      {openFilter === col && (
        <div className="absolute top-5 left-0 z-[100] rounded-lg shadow-2xl p-2 min-w-[160px] max-h-60 overflow-y-auto"
             style={{ background:'#1c1c1e', border:'1px solid #3c3c3e' }}>
          {children}
        </div>
      )}
    </span>
  );
};

// ── Fallback Badge ────────────────────────────────────────────────────────────
const FallbackBadge = ({ row }) => {
  const genFallback    = row.model_fallback_triggered;
  const routeFallback  = row.router_fallback_triggered;
  const genReason      = row.model_fallback_reason   || '';
  const routeReason    = row.router_fallback_reason  || '';

  if (!genFallback && !routeFallback) {
    return (
      <span style={{ background:'#14332a', color:'#4ade80', border:'1px solid #16a34a' }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono">
        ✓ Clean
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {routeFallback && (
        <span title={`Route fallback: ${routeReason}`}
              style={{ background:'#3d1a00', color:'#fb923c', border:'1px solid #ea580c' }}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono cursor-help">
          ⚠ Route
        </span>
      )}
      {genFallback && (
        <span title={`Gen fallback: ${genReason}`}
              style={{ background:'#450a0a', color:'#f87171', border:'1px solid #dc2626' }}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono cursor-help">
          ⚠ Gen
        </span>
      )}
    </div>
  );
};

// ── Model Badge ───────────────────────────────────────────────────────────────
const ModelBadge = ({ modelId, title }) => {
  const s = getModelStyle(modelId);
  if (!modelId) return null;
  return (
    <span title={title || modelId}
          style={{ background: s.bg, color: s.tx, border: `1px solid ${s.bd}` }}
          className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-mono whitespace-nowrap">
      {s.short}
    </span>
  );
};

// ── Model Settings Panel ──────────────────────────────────────────────────────
const ModelSettingsPanel = ({ onClose }) => {
  const adminKey = process.env.REACT_APP_ADMIN_KEY || '';
  const [config, setConfig] = useState(DEFAULT_MODEL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // which field is saving
  const [saved, setSaved] = useState(null);   // which field just saved

  useEffect(() => {
    fetch(`${API}/admin/model-config`, { headers: { 'X-Admin-Key': adminKey } })
      .then(r => r.json())
      .then(d => { setConfig(d.config || DEFAULT_MODEL_CONFIG); setLoading(false); })
      .catch(() => setLoading(false));
  }, [adminKey]);

  const updateModel = async (field, value) => {
    setSaving(field);
    const newCfg = { ...config, [field]: value };
    setConfig(newCfg);
    try {
      await fetch(`${API}/admin/model-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({ [field]: value }),
      });
      setSaved(field);
      setTimeout(() => setSaved(null), 1500);
    } catch (e) {
      console.error('[ModelSettings] save error', e);
    } finally {
      setSaving(null);
    }
  };

  const sections = [
    {
      id:    'routing_model',
      label: 'Routing Model',
      desc:  'Classifies intent and routes to the right agent. Default: Gemini 2.5 Flash Lite (cheapest, ~650ms).',
      opts:  MODEL_OPTIONS.routing,
    },
    {
      id:    'generation_model',
      label: 'Generation Model',
      desc:  'Generates the final response. Non-Claude outputs pass through a normaliser; Sonnet is the quality safety net.',
      opts:  MODEL_OPTIONS.generation,
    },
    {
      id:    'voice_model',
      label: 'Voice Transcription Model',
      desc:  'Transcribes voice notes. Gemini Audio requires GOOGLE_API_KEY.',
      opts:  MODEL_OPTIONS.voice,
    },
  ];

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-end"
         style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="h-full w-[380px] flex flex-col shadow-2xl"
           style={{ background: '#0f0f11', borderLeft: '1px solid #2c2c2e' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#2c2c2e' }}>
          <div>
            <div className="text-sm font-semibold text-white">Model Selector</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Global • Takes effect on next message</div>
          </div>
          <button data-testid="model-settings-close" onClick={onClose}
                  className="text-gray-500 hover:text-white transition-colors text-lg leading-none">×</button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
            {sections.map(sec => {
              const isSaving = saving === sec.id;
              const isSaved  = saved === sec.id;
              const cur = getModelStyle(config[sec.id]);
              return (
                <div key={sec.id} data-testid={`model-section-${sec.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{sec.label}</div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      {isSaving && <span className="text-yellow-400 animate-pulse">saving…</span>}
                      {isSaved  && <span className="text-emerald-400">saved ✓</span>}
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-600 mb-2">{sec.desc}</div>

                  {/* Current badge */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] text-gray-500">Active:</span>
                    <ModelBadge modelId={config[sec.id]}/>
                  </div>

                  {/* Dropdown */}
                  <select
                    data-testid={`model-select-${sec.id}`}
                    value={config[sec.id]}
                    onChange={e => updateModel(sec.id, e.target.value)}
                    disabled={isSaving}
                    className="w-full text-xs px-3 py-2 rounded-lg border outline-none transition-colors"
                    style={{ background: '#1c1c1e', borderColor: isSaving ? '#fbbf24' : '#3c3c3e', color: '#fff', opacity: isSaving ? 0.7 : 1 }}>
                    {sec.opts.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              );
            })}

            {/* Info box */}
            <div className="rounded-lg p-3 text-[10px] text-gray-500 leading-relaxed"
                 style={{ background: '#1a1a1c', border: '1px solid #2c2c2e' }}>
              Non-Claude generation models pass through a normaliser that strips code fences,
              removes comma separators from rupee amounts, and validates action JSON fields.
              If validation fails, the message is retried silently with Sonnet.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Hook Messages Settings Panel ──────────────────────────────────────────────
const HOOK_SOURCES = [
  { key: 'meta',     label: 'Meta',     bg: '#0d2147', tx: '#3b82f6' },
  { key: 'twilio',   label: 'Twilio',   bg: '#0a2e1a', tx: '#22c55e' },
  { key: 'telegram', label: 'Telegram', bg: '#0c2a40', tx: '#38bdf8' },
  { key: 'web',      label: 'Web',      bg: '#1e0a3b', tx: '#a855f7' },
  { key: 'test',     label: 'Test',     bg: '#1a1a1a', tx: '#9ca3af' },
];

const HookMessagesPanel = ({ onClose }) => {
  const adminKey = process.env.REACT_APP_ADMIN_KEY || '';
  const apiBase  = process.env.REACT_APP_BACKEND_URL;
  const [cfg,    setCfg]    = useState(null);
  const [saving, setSaving] = useState(null);   // key being saved
  const [saved,  setSaved]  = useState(null);   // key just saved

  useEffect(() => {
    fetch(`${apiBase}/api/settings/hook-messages`, {
      headers: { 'X-Admin-Key': adminKey }
    })
      .then(r => r.json())
      .then(d => setCfg(d.config))
      .catch(e => console.error('[HookMessages] load error', e));
  }, [adminKey, apiBase]);

  const update = async (key, value) => {
    setSaving(key);
    try {
      const res = await fetch(`${apiBase}/api/settings/hook-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({ [key]: value }),
      });
      const data = await res.json();
      setCfg(data.config);
      setSaved(key);
      setTimeout(() => setSaved(null), 1500);
    } catch (e) {
      console.error('[HookMessages] save error', e);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-end"
         style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div className="h-full w-[360px] flex flex-col shadow-2xl"
           style={{ background: '#0f0f11', borderLeft: '1px solid #2c2c2e' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#2c2c2e' }}>
          <div>
            <div className="text-sm font-semibold text-white">Hook Messages</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Global · Per-channel · Takes effect immediately</div>
          </div>
          <button data-testid="hook-messages-close" onClick={onClose}
                  className="text-gray-500 hover:text-white transition-colors text-lg leading-none">×</button>
        </div>

        {!cfg ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-1">
            {/* Legend */}
            <div className="text-[10px] text-gray-500 mb-4 leading-relaxed">
              <span className="text-gray-400 font-medium">0</span> = no hooks&nbsp;&nbsp;
              <span className="text-gray-400 font-medium">1</span> = ack only&nbsp;&nbsp;
              <span className="text-gray-400 font-medium">2</span> = ack + progress
            </div>

            {HOOK_SOURCES.map(({ key, label, bg, tx }) => {
              const val      = cfg[key] ?? 0;
              const enabled  = val > 0;
              const isSaving = saving === key;
              const isSaved  = saved  === key;
              return (
                <div key={key}
                     data-testid={`hook-row-${key}`}
                     className="flex items-center gap-3 rounded-lg px-3 py-3"
                     style={{ background: '#141416', border: '1px solid #242426' }}>

                  {/* Source badge */}
                  <span style={{ background: bg, color: tx, border: `1px solid ${tx}30`, minWidth: 64 }}
                        className="px-2 py-0.5 rounded text-[10px] font-mono text-center">{label}</span>

                  {/* Toggle */}
                  <button
                    data-testid={`hook-toggle-${key}`}
                    onClick={() => update(key, enabled ? 0 : 1)}
                    disabled={isSaving}
                    className="relative flex-shrink-0 w-8 h-4 rounded-full transition-colors duration-200"
                    style={{ background: enabled ? '#22c55e' : '#3c3c3e' }}>
                    <span className="absolute top-0.5 transition-all duration-200 w-3 h-3 rounded-full bg-white shadow"
                          style={{ left: enabled ? '18px' : '2px' }}/>
                  </button>

                  {/* Count picker — only visible when enabled */}
                  {enabled && (
                    <div className="flex gap-1 ml-auto">
                      {[1, 2].map(n => (
                        <button
                          key={n}
                          data-testid={`hook-count-${key}-${n}`}
                          onClick={() => update(key, n)}
                          disabled={isSaving}
                          className="w-7 h-7 rounded text-[11px] font-semibold transition-all"
                          style={{
                            background: val === n ? tx : '#2c2c2e',
                            color:      val === n ? '#000' : '#9ca3af',
                            border:     `1px solid ${val === n ? tx : '#3c3c3e'}`,
                          }}>{n}</button>
                      ))}
                    </div>
                  )}

                  {/* Status feedback */}
                  <span className="text-[10px] ml-auto" style={{ minWidth: 32 }}>
                    {isSaving && <span className="text-yellow-400 animate-pulse">…</span>}
                    {isSaved  && <span className="text-emerald-400">✓</span>}
                    {!isSaving && !isSaved && <span className="text-gray-600">{val}</span>}
                  </span>
                </div>
              );
            })}

            <div className="rounded-lg p-3 text-[10px] text-gray-500 leading-relaxed mt-4"
                 style={{ background: '#1a1a1c', border: '1px solid #2c2c2e' }}>
              Hook 1 is the instant acknowledgement ("Got it&nbsp;👍").
              Hook 2 is the progress message ("Checking your records…").
              Web and Test default to 0 to keep chat interfaces clean.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
// URL-state persistence for the Live Log toolbar.
// Scope: overrideOnly, showLast, search (the three highest-value triage filters).
// We keep this serialiser tiny and self-contained so it can survive
// the future component-decomposition without spreading state plumbing.
const _urlReadInitial = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      overrideOnly: sp.get('overrides') === '1',
      showLast:     sp.get('showLast') ?? '10',
      search:       sp.get('search') ?? '',
    };
  } catch {
    return { overrideOnly: false, showLast: '10', search: '' };
  }
};
const _urlWrite = ({ overrideOnly, showLast, search }) => {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (overrideOnly) sp.set('overrides', '1'); else sp.delete('overrides');
    if (showLast && showLast !== '10') sp.set('showLast', showLast); else sp.delete('showLast');
    if (search) sp.set('search', search); else sp.delete('search');
    const qs = sp.toString();
    const next = `${window.location.pathname}${qs ? '?' + qs : ''}`;
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, '', next);
    }
  } catch (e) {
    console.warn('[LiveLog] URL state write failed:', e);
  }
};

const ConversationLogPage = () => {
  // Read initial URL state ONCE on mount (lazy initialiser).
  const _initial = useMemo(_urlReadInitial, []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [pendingRows, setPendingRows] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [replayUser, setReplayUser] = useState(null);
  const [globalSearch, setGlobalSearch] = useState(_initial.search);
  const [openFilter, setOpenFilter] = useState(null);
  const [filters, setFilters] = useState({ ...INIT_FILTERS, overrideOnly: _initial.overrideOnly });
  const [showSettings, setShowSettings] = useState(false);
  const [showHookPanel, setShowHookPanel] = useState(false);
  const [showLast, setShowLast] = useState(_initial.showLast);  // '10' | '15' | '20' | '25' | '50' | 'custom' | 'off'
  const [customLimit, setCustomLimit] = useState(30);

  // Persist the three triage filters back to URL whenever they change.
  // Replaces (not pushes) history entry so the back button isn't polluted.
  useEffect(() => {
    _urlWrite({
      overrideOnly: filters.overrideOnly,
      showLast,
      search: globalSearch,
    });
  }, [filters.overrideOnly, showLast, globalSearch]);

  const autoScrollRef = useRef(true);
  const bottomRef = useRef(null);
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  useEffect(() => { autoScrollRef.current = autoScroll; }, [autoScroll]);

  // ── Load initial data + re-fetch when Show Last N changes ──────────────────
  // (effect moved below loadLogs definition so loadLogs is in scope at hook-eval time)

  const loadLogs = useCallback(async (offset = 0, replace = false, limitOverride = null) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    try {
      const adminKey = process.env.REACT_APP_ADMIN_KEY || '';
      const limit = limitOverride || 200;
      const r = await fetch(
        `${API}/admin/live-log?limit=${limit}&offset=${offset}`,
        { headers: { 'X-Admin-Key': adminKey } }
      );
      const d = await r.json();
      const incoming = d.rows || [];
      if (replace) setRows(incoming);
      else setRows(prev => [...prev, ...incoming]);
      // Only enable infinite scroll in default mode (not Show Last N mode)
      setHasMore(limitOverride === null && incoming.length === 200);
    } catch (e) {
      console.error('[LiveLog] load error', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // ── Load initial data + re-fetch when Show Last N changes ──────────────────
  // Placed AFTER loadLogs because the loadLogs useCallback must be defined
  // before this hoist-sensitive effect runs.
  useEffect(() => {
    if (showLast === 'off') {
      loadLogs(0, true, null);
    } else {
      const n = showLast === 'custom'
        ? Math.max(1, parseInt(customLimit, 10) || 10)
        : parseInt(showLast, 10);
      loadLogs(0, true, n);
    }
    // customLimit is intentionally excluded — the dedicated effect below
    // handles ongoing customLimit edits while in custom mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLast, loadLogs]);

  // Re-fetch when custom limit value changes (while custom mode is active)
  useEffect(() => {
    if (showLast === 'custom' && customLimit > 0) {
      const n = Math.max(1, parseInt(customLimit, 10) || 10);
      loadLogs(0, true, n);
    }
    // showLast is intentionally excluded — the effect above handles mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customLimit, loadLogs]);

  // ── Supabase Realtime + polling fallback ──────────────────────────────────
  // Pattern: realtime SUBSCRIBED → stop polling. CHANNEL_ERROR / TIMED_OUT /
  // CLOSED → start 5s polling. Mirrors the ChatPage fallback. This makes the
  // page resilient to RLS-induced channel errors when the anon supabase client
  // has no authenticated session.
  useEffect(() => {
    if (!supabase) return;

    let reconnectTimer = null;
    let pollTimer      = null;
    let currentChannel = null;
    let currentLimit   = null;       // mirrors showLast at the time polling kicks in

    const computeLimit = () => {
      if (showLast === 'off')    return null;
      if (showLast === 'custom') return Math.max(1, parseInt(customLimit, 10) || 10);
      return parseInt(showLast, 10);
    };

    const startPoll = () => {
      if (pollTimer) return;
      currentLimit = computeLimit();
      pollTimer = setInterval(() => {
        loadLogs(0, true, currentLimit);
      }, 5000);
    };
    const stopPoll = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const subscribe = () => {
      if (currentChannel) {
        try { supabase.removeChannel(currentChannel); }
        catch (err) { console.warn('[LiveLog] removeChannel (resubscribe) failed:', err); }
      }
      // NOTE: No server-side filter — requires REPLICA IDENTITY FULL which may
      // not be set. Filtering client-side instead for reliable connection.
      currentChannel = supabase.channel('live-log-' + Date.now())
        .on('postgres_changes', {
          event: 'INSERT', schema: 'public', table: 'conversations',
        }, (payload) => {
          if (!payload.new) return;
          // Show rows from all channels (whatsapp, telegram, web)
          const nr = { ...payload.new, user_name: '...', user_phone: '', user_role: '' };
          if (autoScrollRef.current) setRows(prev => [nr, ...prev]);
          else setPendingRows(prev => [nr, ...prev]);
        })
        .subscribe((status) => {
          const connected = status === 'SUBSCRIBED';
          setIsLive(connected);
          if (connected) {
            stopPoll();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            startPoll();
          }
        });
    };

    subscribe();

    // Auto-reconnect every 30s if still disconnected
    reconnectTimer = setInterval(() => {
      setIsLive(live => {
        if (!live) subscribe();
        return live;
      });
    }, 30000);

    return () => {
      if (reconnectTimer) clearInterval(reconnectTimer);
      stopPoll();
      if (currentChannel) {
        try { supabase.removeChannel(currentChannel); }
        catch (err) { console.warn('[LiveLog] removeChannel (cleanup) failed:', err); }
      }
    };
    // showLast/customLimit/loadLogs are read inside computeLimit only when
    // polling kicks in; the closure captures the live values via the
    // recomputed `currentLimit`. Re-subscribing on every showLast change would
    // churn the realtime connection unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Infinite scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!bottomRef.current || !hasMore || loadingMore) return;
    const obs = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadLogs(rows.length); },
      { threshold: 0.1 }
    );
    obs.observe(bottomRef.current);
    return () => obs.disconnect();
  }, [rows.length, hasMore, loadingMore, loadLogs]);

  // ── Agent stats + timing avg — computed from currently-displayed rows ─────
  // (definitions below, after filteredRows is declared)

  // ── Client-side filtering ───────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const gs = globalSearch.toLowerCase();
    // "Last N" mode: bypass date filter (backend already fetched exactly N rows)
    const lastNMode = showLast !== 'off';

    const passed = rows.filter(r => {
      // Time — skipped entirely when Last-N mode is active so we can pull
      // entries from previous months/years if needed.
      if (!lastNMode && filters.time !== 'all') {
        const d = new Date(r.created_at), now = new Date();
        if (filters.time === 'today' && d.toDateString() !== now.toDateString()) return false;
        if (filters.time === 'week' && d < new Date(now - 7*86400000)) return false;
        if (filters.time === 'month' && d < new Date(now - 30*86400000)) return false;
      }
      // Global search
      if (gs && ![r.original_content,r.english_translation,r.haiku_intent_summary,r.sonnet_response]
                      .some(f => f?.toLowerCase().includes(gs))) return false;
      // Text filters
      if (filters.origMsg && !r.original_content?.toLowerCase().includes(filters.origMsg.toLowerCase())) return false;
      if (filters.translation && !r.english_translation?.toLowerCase().includes(filters.translation.toLowerCase())) return false;
      if (filters.haikuIntent && !r.haiku_intent_summary?.toLowerCase().includes(filters.haikuIntent.toLowerCase())) return false;
      if (filters.sonnetResp && !r.sonnet_response?.toLowerCase().includes(filters.sonnetResp.toLowerCase())) return false;
      if (filters.userName && !r.user_name?.toLowerCase().includes(filters.userName.toLowerCase())) return false;
      if (filters.phone && !r.user_phone?.includes(filters.phone)) return false;
      // Multi-select
      if (filters.roles.length > 0 && !filters.roles.includes(r.user_role)) return false;
      if (filters.channels.length > 0) {
        // Normalise legacy 'whatsapp' / 'web_log' values to their display keys
        const ch = r.channel === 'whatsapp' ? 'twilio' : r.channel === 'web_log' ? 'web' : (r.channel || 'twilio');
        if (!filters.channels.includes(ch)) return false;
      }
      if (filters.languages.length > 0 && !filters.languages.includes(r.detected_language?.toLowerCase())) return false;
      if (filters.routing.length > 0) {
        const m = r.confidence_gate_triggered
          ? 'GATE'
          : (ROUTING_LABELS[r.routing_method]?.label || (r.routing_method ? 'UNK' : 'CONF'));
        if (!filters.routing.includes(m)) return false;
      }
      // Override-only chip: hide baseline-routed rows
      if (filters.overrideOnly) {
        if (r.confidence_gate_triggered) return false;  // gate is a router signal, not an override
        if (!r.routing_method) return false;
        if (BASELINE_ROUTING.has(r.routing_method)) return false;
      }
      if (filters.finalAgents.length > 0 && !filters.finalAgents.includes(r.agent_routed_final)) return false;
      // Confidence
      if (filters.confidence !== 'all') {
        const c = r.haiku_confidence || 0;
        if (filters.confidence === 'high' && c < 0.8) return false;
        if (filters.confidence === 'medium' && (c < 0.6 || c >= 0.8)) return false;
        if (filters.confidence === 'low' && c >= 0.6) return false;
      }
      // Haiku time
      if (filters.haikuTime !== 'all') {
        const ms = r.response_time_haiku_ms || 0;
        if (filters.haikuTime === 'fast' && ms >= 500) return false;
        if (filters.haikuTime === 'medium' && (ms < 500 || ms > 1000)) return false;
        if (filters.haikuTime === 'slow' && ms <= 1000) return false;
      }
      // Sonnet time
      if (filters.sonnetTime !== 'all') {
        const ms = r.response_time_sonnet_ms || 0;
        if (filters.sonnetTime === 'fast' && ms >= 1000) return false;
        if (filters.sonnetTime === 'medium' && (ms < 1000 || ms > 3000)) return false;
        if (filters.sonnetTime === 'slow' && ms <= 3000) return false;
      }
      // Total time
      if (filters.totalTime !== 'all') {
        const ms = r.response_time_total_ms || 0;
        if (filters.totalTime === 'fast' && ms >= 3000) return false;
        if (filters.totalTime === 'medium' && (ms < 3000 || ms > 7000)) return false;
        if (filters.totalTime === 'slow' && ms <= 7000) return false;
      }
      // Timing breakdown filters
      for (const tc of TIMING_BREAKDOWN_COLS) {
        if (filters[tc.key] !== 'all') {
          const ms = r[tc.field] || 0;
          if (filters[tc.key] === 'fast' && ms >= tc.t1) return false;
          if (filters[tc.key] === 'medium' && (ms < tc.t1 || ms > tc.t2)) return false;
          if (filters[tc.key] === 'slow' && ms <= tc.t2) return false;
        }
      }
      // Entry
      if (filters.entry === 'entry' && !r.entry_id) return false;
      if (filters.entry === 'no-entry' && r.entry_id) return false;
      if (filters.entry === 'errors' && !r.error_message) return false;
      return true;
    });

    if (lastNMode) {
      // Backend already returned exactly N rows sorted newest-first.
      // Still apply other active filters (text search, role, etc.) on top.
      return passed;
    }
    return passed;
  }, [rows, filters, globalSearch, showLast]);

  // ── Agent stats — computed from the currently-displayed (filtered) rows ──
  // This keeps the status bar responsive to the user's current selection and
  // prevents the bar from going empty after midnight when the calendar date
  // rolls over but the fetched rows are still from the previous day.
  const agentStats = useMemo(() => {
    const source = filteredRows;
    const counts = {}; const names = {};
    source.forEach(r => {
      if (r.agent_routed_final) {
        counts[r.agent_routed_final] = (counts[r.agent_routed_final] || 0) + 1;
        names[r.agent_routed_final] = r.agent_routed_name || `Agent ${r.agent_routed_final}`;
      }
    });
    const sorted = Object.entries(counts).sort(([,a],[,b]) => b-a)
      .map(([id, cnt]) => ({ id:+id, name:names[id], cnt, pct: Math.round(cnt/(source.length||1)*100) }));
    const avgMs = source.reduce((s,r) => s+(r.response_time_total_ms||0), 0) / (source.length||1);
    const fallbackCount = source.filter(r => r.model_fallback_triggered || r.router_fallback_triggered).length;
    const fallbackRate = source.length > 0 ? Math.round((fallbackCount / source.length) * 100) : 0;
    return { top:sorted.slice(0,5), rest:Math.max(0,sorted.length-5), total:source.length, avgMs:Math.round(avgMs), fallbackRate, fallbackCount };
  }, [filteredRows]);

  // ── Timing avg breakdown (memoized) ──────────────────────────────────────
  const timingAvg = useMemo(() => {
    const tRows = filteredRows.filter(r => r.timing_sum_ms != null);
    if (tRows.length === 0) return null;
    const avg = (f) => Math.round(tRows.reduce((s,r) => s+(r[f]||0), 0) / tRows.length);
    return {
      userFetch: avg('user_fetch_ms'), haiku: avg('response_time_haiku_ms'),
      sonnet: avg('response_time_sonnet_ms'), twilio: avg('twilio_send_ms'), other: avg('timing_gap_ms'),
    };
  }, [filteredRows]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleFilter = (key, val) => setFilters(f => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
  }));

  const clearAll = () => { setFilters(INIT_FILTERS); setGlobalSearch(''); setOpenFilter(null); };

  const hasActiveFilters = Object.entries(filters).some(([k,v]) =>
    Array.isArray(v) ? v.length > 0 : (k === 'time' ? v !== 'today' : v !== 'all' && v !== '')
  ) || globalSearch;

  const exportCSV = () => {
    const hdrs = ['#','Time','User','Phone','Role','Language','Message','Translation',
      'Routing Decision','Routing','Agents','Final Agent',
      'Routing Model','Generation Model','Fallback Status','Fallback Reason',
      'Confidence%','Routing ms','Generation Response','Generation ms','Total ms',
      ...TIMING_BREAKDOWN_COLS.map(tc => tc.label), 'Entry','Error'];
    const data = filteredRows.map((r,i) => [
      i+1, fmtTs(r.created_at), r.user_name||'', r.user_phone||'', r.user_role||'',
      r.detected_language||'', r.original_content||'', r.english_translation||'',
      r.haiku_intent_summary||'', r.routing_method||'',
      (r.agents_considered||[]).map(a=>`#${a.agent_id}`).join(';'),
      r.agent_routed_name ? `#${r.agent_routed_final} ${r.agent_routed_name}` : '',
      r.router_model_used || '',
      r.model_used || '',
      r.model_fallback_triggered ? 'FALLBACK' : (r.router_fallback_triggered ? 'ROUTE-FALLBACK' : 'Clean'),
      r.model_fallback_reason || r.router_fallback_reason || '',
      r.haiku_confidence ? Math.round(r.haiku_confidence*100) : '',
      r.response_time_haiku_ms||'', r.sonnet_response||'',
      r.response_time_sonnet_ms||'', r.response_time_total_ms||'',
      ...TIMING_BREAKDOWN_COLS.map(tc => r[tc.field] ?? ''),
      r.bill_number||r.entry_id||'', r.error_message||'',
    ]);
    const csv = [hdrs,...data].map(row=>row.map(c=>`"${String(c||'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = `noteho-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // ── Column filter dropdown component (inline) ───────────────────────────────
  // ColFilter is defined above as a standalone component

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center h-screen" style={{ background:'#000' }}>
      <div className="text-center">
        <div className="w-6 h-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mx-auto mb-3"/>
        <span className="text-gray-400 text-sm">Loading conversation log…</span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background:'#000', color:'#fff', fontFamily:"'SF Pro Display', -apple-system, sans-serif" }}
         data-testid="live-log-page">

      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2.5 shrink-0 z-10 flex-wrap gap-y-2"
           style={{ background:'#0a0a0a', borderBottom:'1px solid #2c2c2e' }}>
        {/* Live indicator */}
        <div data-testid="live-indicator" className="flex items-center gap-1.5 shrink-0">
          <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-emerald-400 animate-pulse' : 'bg-red-500'}`}/>
          <span className={`text-[11px] font-mono font-semibold ${isLive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isLive ? 'LIVE' : 'DISCONNECTED'}
          </span>
        </div>

        <div className="text-gray-600 text-xs shrink-0">|</div>

        <span className="text-gray-400 text-xs shrink-0">
          Showing <span className="text-white font-mono">{filteredRows.length}</span> of <span className="text-white font-mono">{rows.length}</span>
        </span>

        <button data-testid="back-btn" onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-white text-xs shrink-0 transition-colors">← Dashboard</button>

        {/* Global search */}
        <div className="flex-1 min-w-[180px] max-w-xs">
          <input data-testid="global-search"
                 value={globalSearch}
                 onChange={e => setGlobalSearch(e.target.value)}
                 placeholder="Search all fields…"
                 className="w-full bg-transparent border rounded-lg px-3 py-1.5 text-xs outline-none text-white placeholder-gray-600 transition-colors"
                 style={{ borderColor: globalSearch ? '#3b82f6' : '#2c2c2e' }}/>
        </div>

        <div className="flex items-center gap-2 ml-auto shrink-0 flex-wrap gap-y-1">
          {/* Show Last N messages */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">Show last</span>
            <select data-testid="show-last-dropdown"
                    value={showLast}
                    onChange={e => setShowLast(e.target.value)}
                    className="text-xs rounded-lg px-2 py-1.5 border outline-none"
                    style={{ background:'#1c1c1e', borderColor: showLast !== 'off' ? '#7c3aed' : '#3c3c3e', color:'#fff' }}>
              <option value="10">10 messages</option>
              <option value="15">15 messages</option>
              <option value="20">20 messages</option>
              <option value="25">25 messages</option>
              <option value="50">50 messages</option>
              <option value="custom">Custom…</option>
              <option value="off">Off (use date filter)</option>
            </select>
            {showLast === 'custom' && (
              <input data-testid="show-last-custom"
                     type="number"
                     min="1"
                     max="2000"
                     value={customLimit}
                     onChange={e => setCustomLimit(e.target.value)}
                     className="w-20 text-xs rounded-lg px-2 py-1.5 border outline-none"
                     style={{ background:'#1c1c1e', borderColor:'#7c3aed', color:'#fff' }}
                     placeholder="e.g. 35"/>
            )}
          </div>

          {/* Date filter — inactive when Last-N mode is on */}
          <select data-testid="date-filter"
                  value={filters.time}
                  onChange={e => setFilters(f => ({...f, time:e.target.value}))}
                  disabled={showLast !== 'off'}
                  title={showLast !== 'off' ? 'Disabled while "Show last N" is active' : ''}
                  className="text-xs rounded-lg px-2 py-1.5 border outline-none transition-opacity"
                  style={{
                    background:'#1c1c1e',
                    borderColor:'#3c3c3e',
                    color:'#fff',
                    opacity: showLast !== 'off' ? 0.4 : 1,
                    cursor: showLast !== 'off' ? 'not-allowed' : 'pointer',
                  }}>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="all">All Time</option>          </select>

          {/* Override-only quick chip — pilot debugging shortcut */}
          <button data-testid="override-only-toggle"
                  onClick={() => setFilters(f => ({...f, overrideOnly: !f.overrideOnly}))}
                  title="Show only deterministic-override rows (refusals, rule setters, worker onboarding, query templates, corrections, etc.)"
                  className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors"
                  style={{
                    borderColor: filters.overrideOnly ? '#b91c1c' : '#2c2c2e',
                    color:       filters.overrideOnly ? '#fca5a5' : '#9ca3af',
                    background:  filters.overrideOnly ? '#3b131320' : 'transparent',
                  }}>
            <span style={{ fontSize: '10px' }}>{filters.overrideOnly ? '◉' : '○'}</span>
            Overrides only
          </button>

          {hasActiveFilters && (
            <button data-testid="clear-filters" onClick={clearAll}
                    className="text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors text-orange-400 border-orange-400/30 hover:bg-orange-400/10">
              Clear All
            </button>
          )}

          {/* Auto-scroll toggle */}
          <button data-testid="autoscroll-toggle"
                  onClick={() => setAutoScroll(p => !p)}
                  className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors ${autoScroll ? 'text-emerald-400 border-emerald-400/30' : 'text-gray-500 border-gray-700'}`}>
            {autoScroll ? '⬆ Auto' : '⬆ Paused'}
          </button>

          <button data-testid="export-csv" onClick={exportCSV}
                  className="text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 transition-colors">
            Export CSV
          </button>

          {/* Model Selector */}
          <button data-testid="model-settings-btn"
                  onClick={() => setShowSettings(p => !p)}
                  title="Model Selector"
                  className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors"
                  style={{
                    borderColor: showSettings ? '#7c3aed' : '#2c2c2e',
                    color: showSettings ? '#c084fc' : '#9ca3af',
                    background: showSettings ? '#2d1b6920' : 'transparent',
                  }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            Models
          </button>

          {/* Hook Messages Settings */}
          <button data-testid="hook-messages-btn"
                  onClick={() => setShowHookPanel(p => !p)}
                  title="Hook Messages — control ack/progress per channel"
                  className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors"
                  style={{
                    borderColor: showHookPanel ? '#22c55e' : '#2c2c2e',
                    color: showHookPanel ? '#4ade80' : '#9ca3af',
                    background: showHookPanel ? '#0a2e1a' : 'transparent',
                  }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Hooks
          </button>
        </div>
      </div>

      {/* ── Agent Summary Bar ────────────────────────────────────────────── */}
      <AgentSummaryBar stats={agentStats} timingAvg={timingAvg} onAgentFilter={id => {
        setFilters(f => ({
          ...f,
          finalAgents: f.finalAgents.includes(id) ? f.finalAgents.filter(x=>x!==id) : [...f.finalAgents, id]
        }));
      }}/>

      {/* ── Pending pill ─────────────────────────────────────────────────── */}
      {!autoScroll && pendingRows.length > 0 && (
        <div className="flex justify-center py-1.5 shrink-0" style={{ background:'#0a0a0a' }}>
          <button data-testid="pending-pill"
                  onClick={() => { setRows(p=>[...pendingRows,...p]); setPendingRows([]); setAutoScroll(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono transition-all"
                  style={{ background:'#1d3461', color:'#60a5fa', border:'1px solid #2563eb' }}>
            ▲ {pendingRows.length} new message{pendingRows.length!==1?'s':''} — click to load
          </button>
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto" data-testid="log-table-container">
        <table className="w-full text-xs border-collapse" style={{ minWidth:'3200px' }}>
          <thead className="sticky top-0 z-20" style={{ background:'#0f0f0f' }}>
            <tr>
              {[
                { key:'#', label:'#', w:44 },
                { key:'time', label:'Time', w:160 },
                { key:'userName', label:'User Name', w:120 },
                { key:'phone', label:'Phone', w:100 },
                { key:'channel', label:'Channel', w:90 },
                { key:'role', label:'Role', w:90 },
                { key:'lang', label:'Language', w:100 },
                { key:'origMsg', label:'Original Message', w:200 },
                { key:'translation', label:'Translation', w:200 },
                { key:'haikuIntent', label:'Routing Decision', w:180 },
                { key:'routing', label:'Routing', w:80 },
                { key:'agents', label:'Agents', w:160 },
                { key:'finalAgent', label:'Final Agent', w:160 },
                { key:'routingModel', label:'Routing Model', w:100 },
                { key:'generationModel', label:'Generation Model', w:110 },
                { key:'fallbackStatus', label:'Fallback', w:120 },
                { key:'confidence', label:'Confidence', w:100 },
                { key:'haikuTime', label:'Routing ms', w:92 },
                { key:'sonnetResp', label:'Generation Response', w:220 },
                { key:'sonnetTime', label:'Generation ms', w:100 },
                { key:'totalTime', label:'Total ms', w:88 },
                ...TIMING_BREAKDOWN_COLS.map(tc => ({ key:tc.key, label:tc.label, w:tc.w })),
                { key:'entry', label:'Entry', w:100 },
              ].map(col => (
                <th key={col.key}
                    style={{ width:col.w, minWidth:col.w, borderBottom:'1px solid #2c2c2e', borderRight:'1px solid #1c1c1e', padding:'6px 8px', textAlign:'left', fontWeight:500, color:'#9ca3af', letterSpacing:'0.03em', userSelect:'none' }}>
                  <div className="flex items-center gap-0.5">
                    <span>{col.label}</span>

                    {/* Per-column filter dropdowns */}
                    {col.key === 'userName' && (
                      <ColFilter col="userName" active={!!filters.userName} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        <input autoFocus value={filters.userName} onChange={e=>setFilters(f=>({...f,userName:e.target.value}))}
                               placeholder="Search name…" className="w-full bg-transparent text-white text-xs px-2 py-1.5 border border-gray-700 rounded outline-none"/>
                      </ColFilter>
                    )}
                    {col.key === 'phone' && (
                      <ColFilter col="phone" active={!!filters.phone} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        <input autoFocus value={filters.phone} onChange={e=>setFilters(f=>({...f,phone:e.target.value}))}
                               placeholder="Phone digits…" className="w-full bg-transparent text-white text-xs px-2 py-1.5 border border-gray-700 rounded outline-none"/>
                      </ColFilter>
                    )}
                    {col.key === 'channel' && (
                      <ColFilter col="channel" active={filters.channels.length>0} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {[
                          { val:'meta',     label:'Meta',     bg:'#0d2147', tx:'#3b82f6' },
                          { val:'twilio',   label:'Twilio',   bg:'#0a2e1a', tx:'#22c55e' },
                          { val:'telegram', label:'Telegram', bg:'#0c2a40', tx:'#38bdf8' },
                          { val:'web',      label:'Web',      bg:'#1e0a3b', tx:'#a855f7' },
                          { val:'test',     label:'Test',     bg:'#1a1a1a', tx:'#9ca3af' },
                        ].map(({ val, label, bg, tx }) => (
                          <label key={val} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="checkbox" checked={filters.channels.includes(val)}
                                   onChange={() => toggleFilter('channels', val)} className="accent-blue-500"/>
                            <span style={{ background: bg, color: tx, border:`1px solid ${tx}30` }}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-mono">{label}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'role' && (
                      <ColFilter col="role" active={filters.roles.length>0} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {['owner','employee','customer'].map(r=>(
                          <label key={r} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="checkbox" checked={filters.roles.includes(r)} onChange={()=>toggleFilter('roles',r)} className="accent-blue-500"/>
                            <span className="text-white capitalize">{r}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'lang' && (
                      <ColFilter col="lang" active={filters.languages.length>0} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {LANGUAGES.map(l=>(
                          <label key={l} className="flex items-center gap-2 px-1 py-0.5 hover:bg-white/5 rounded cursor-pointer">
                            <input type="checkbox" checked={filters.languages.includes(l)} onChange={()=>toggleFilter('languages',l)} className="accent-blue-500"/>
                            <span className="text-white capitalize text-[11px]">{l}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'origMsg' && (
                      <ColFilter col="origMsg" active={!!filters.origMsg} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        <input autoFocus value={filters.origMsg} onChange={e=>setFilters(f=>({...f,origMsg:e.target.value}))}
                               placeholder="Search…" className="w-full bg-transparent text-white text-xs px-2 py-1.5 border border-gray-700 rounded outline-none"/>
                      </ColFilter>
                    )}
                    {col.key === 'translation' && (
                      <ColFilter col="translation" active={!!filters.translation} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        <input autoFocus value={filters.translation} onChange={e=>setFilters(f=>({...f,translation:e.target.value}))}
                               placeholder="Search…" className="w-full bg-transparent text-white text-xs px-2 py-1.5 border border-gray-700 rounded outline-none"/>
                      </ColFilter>
                    )}
                    {col.key === 'haikuIntent' && (
                      <ColFilter col="haikuIntent" active={!!filters.haikuIntent} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        <input autoFocus value={filters.haikuIntent} onChange={e=>setFilters(f=>({...f,haikuIntent:e.target.value}))}
                               placeholder="Search intent…" className="w-full bg-transparent text-white text-xs px-2 py-1.5 border border-gray-700 rounded outline-none"/>
                      </ColFilter>
                    )}
                    {col.key === 'routing' && (
                      <ColFilter col="routing" active={filters.routing.length>0} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {['EXCL','CONF','FALL','GATE','REFUSE','RULE','WORKER','CORR','CORR-T','QTPL','PEND','INVITE','UNK'].map(r=>(
                          <label key={r} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="checkbox" checked={filters.routing.includes(r)} onChange={()=>toggleFilter('routing',r)} className="accent-blue-500"/>
                            <span className="text-white font-mono text-[11px]">{r}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'confidence' && (
                      <ColFilter col="confidence" active={filters.confidence!=='all'} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {[['all','All'],['high','High ≥80%'],['medium','Medium 60-79%'],['low','Low <60%']].map(([v,l])=>(
                          <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="radio" name="conf" checked={filters.confidence===v} onChange={()=>setFilters(f=>({...f,confidence:v}))} className="accent-blue-500"/>
                            <span className="text-white text-[11px]">{l}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'haikuTime' && (
                      <ColFilter col="haikuTime" active={filters.haikuTime!=='all'} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {[['all','All'],['fast','Fast <500ms'],['medium','Medium'],['slow','Slow >1s']].map(([v,l])=>(
                          <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="radio" name="ht" checked={filters.haikuTime===v} onChange={()=>setFilters(f=>({...f,haikuTime:v}))} className="accent-blue-500"/>
                            <span className="text-white text-[11px]">{l}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'sonnetResp' && (
                      <ColFilter col="sonnetResp" active={!!filters.sonnetResp} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        <input autoFocus value={filters.sonnetResp} onChange={e=>setFilters(f=>({...f,sonnetResp:e.target.value}))}
                               placeholder="Search response…" className="w-full bg-transparent text-white text-xs px-2 py-1.5 border border-gray-700 rounded outline-none"/>
                      </ColFilter>
                    )}
                    {col.key === 'sonnetTime' && (
                      <ColFilter col="sonnetTime" active={filters.sonnetTime!=='all'} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {[['all','All'],['fast','Fast <1s'],['medium','Medium'],['slow','Slow >3s']].map(([v,l])=>(
                          <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="radio" name="st" checked={filters.sonnetTime===v} onChange={()=>setFilters(f=>({...f,sonnetTime:v}))} className="accent-blue-500"/>
                            <span className="text-white text-[11px]">{l}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'totalTime' && (
                      <ColFilter col="totalTime" active={filters.totalTime!=='all'} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {[['all','All'],['fast','Fast <3s'],['medium','Medium'],['slow','Slow >7s']].map(([v,l])=>(
                          <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="radio" name="tt" checked={filters.totalTime===v} onChange={()=>setFilters(f=>({...f,totalTime:v}))} className="accent-blue-500"/>
                            <span className="text-white text-[11px]">{l}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {col.key === 'entry' && (
                      <ColFilter col="entry" active={filters.entry!=='all'} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                        {[['all','All'],['entry','Entry created'],['no-entry','No entry'],['errors','Errors only']].map(([v,l])=>(
                          <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                            <input type="radio" name="en" checked={filters.entry===v} onChange={()=>setFilters(f=>({...f,entry:v}))} className="accent-blue-500"/>
                            <span className="text-white text-[11px]">{l}</span>
                          </label>
                        ))}
                      </ColFilter>
                    )}
                    {/* Timing breakdown column filters */}
                    {(() => {
                      const tc = TIMING_BREAKDOWN_COLS.find(tc => tc.key === col.key);
                      if (!tc) return null;
                      return (
                        <ColFilter col={tc.key} active={filters[tc.key]!=='all'} openFilter={openFilter} setOpenFilter={setOpenFilter}>
                          {[['all','All'],['fast',`Fast <${tc.t1}ms`],['medium','Medium'],['slow',`Slow >${tc.t2}ms`]].map(([v,l])=>(
                            <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
                              <input type="radio" name={tc.key} checked={filters[tc.key]===v} onChange={()=>setFilters(f=>({...f,[tc.key]:v}))} className="accent-blue-500"/>
                              <span className="text-white text-[11px]">{l}</span>
                            </label>
                          ))}
                        </ColFilter>
                      );
                    })()}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={32} className="text-center py-16 text-gray-500">
                  {rows.length === 0 ? 'No messages yet — send a message via WhatsApp, Telegram, or Web to see it here instantly' : 'No results — try clearing filters or scrolling down to load older records'}
                </td>
              </tr>
            ) : (
              filteredRows.map((row, idx) => {
                const cl = CC[getCluster(row.agent_routed_final)] || CC[1];
                const rs = ROLE_STYLE[row.user_role] || { bg:'#1c1c1e', tx:'#9ca3af' };
                const hasError = !!row.error_message;
                const hasFallback = !!(row.model_fallback_triggered || row.router_fallback_triggered);
                const isNew = idx === 0;
                return (
                  <tr key={row.id || idx}
                      data-testid={`log-row-${idx}`}
                      onClick={() => setExpandedRow(row)}
                      className={`cursor-pointer border-b transition-colors ${isNew ? 'animate-pulse-once' : ''} hover:bg-white/5`}
                      style={{ borderColor:'#1a1a1a', background: hasError ? '#1a0505' : hasFallback ? '#1a0a00' : 'transparent' }}>

                    {/* # */}
                    <td className="px-2 py-2 text-center text-gray-600 font-mono text-[10px]" style={{ borderRight:'1px solid #111' }}>{idx+1}</td>

                    {/* Time */}
                    <td className="px-2 py-2 text-[10px] font-mono text-gray-400 whitespace-nowrap" style={{ borderRight:'1px solid #111' }}>{fmtTs(row.created_at)}</td>

                    {/* User Name */}
                    <td className="px-2 py-2 text-xs text-white font-medium" style={{ borderRight:'1px solid #111' }}>
                      {row.user_name || <span className="text-gray-600">—</span>}
                    </td>

                    {/* Phone */}
                    <td className="px-2 py-2 text-[11px] font-mono text-gray-400" style={{ borderRight:'1px solid #111' }}>{maskPhone(row.user_phone)}</td>

                    {/* Channel */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }}>
                      {row.channel === 'meta'
                        ? <span style={{ background:'#0d2147', color:'#3b82f6', border:'1px solid #3b82f630' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono">Meta</span>
                        : row.channel === 'twilio' || row.channel === 'whatsapp'
                          ? <span style={{ background:'#0a2e1a', color:'#22c55e', border:'1px solid #22c55e30' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono">Twilio</span>
                          : row.channel === 'telegram'
                            ? <span style={{ background:'#0c2a40', color:'#38bdf8', border:'1px solid #38bdf830' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono">Telegram</span>
                            : row.channel === 'web' || row.channel === 'web_log'
                              ? <span style={{ background:'#1e0a3b', color:'#a855f7', border:'1px solid #a855f730' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono">Web</span>
                              : row.channel === 'test'
                                ? <span style={{ background:'#1a1a1a', color:'#9ca3af', border:'1px solid #4b556330' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono">Test</span>
                                : <span style={{ background:'#1a1a1a', color:'#6b7280', border:'1px solid #37415130' }} className="px-1.5 py-0.5 rounded text-[10px] font-mono">{row.channel || '—'}</span>
                      }
                    </td>

                    {/* Role */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }}>
                      {row.user_role && row.user_role !== 'unknown'
                        ? <span style={{ background:rs.bg, color:rs.tx, border:`1px solid ${rs.tx}30` }} className="px-1.5 py-0.5 rounded text-[10px] font-mono capitalize">{row.user_role}</span>
                        : <span className="text-gray-600 text-xs">—</span>
                      }
                    </td>

                    {/* Language */}
                    <td className="px-2 py-2 text-[11px] text-gray-300 capitalize" style={{ borderRight:'1px solid #111' }}>
                      {row.detected_language ? `🇮🇳 ${row.detected_language}` : '—'}
                    </td>

                    {/* Original Message */}
                    <td className="px-2 py-2 text-xs text-gray-200" style={{ borderRight:'1px solid #111', maxWidth:220 }} title={row.original_content}>
                      <div className="flex items-center gap-1.5">
                        <InputTypeBadge type={row.input_type} compact={true}/>
                        <span className="break-words">{truncate(row.original_content, 55)}</span>
                      </div>
                      {row.media_transcription && row.input_type === 'voice' && (
                        <div className="text-[10px] text-purple-300/80 mt-0.5 italic" title={row.media_transcription}>
                          🎙 {truncate(row.media_transcription, 55)}
                        </div>
                      )}
                    </td>

                    {/* Translation */}
                    <td className="px-2 py-2 text-xs text-gray-400" style={{ borderRight:'1px solid #111', background:'#111', maxWidth:200 }} title={row.english_translation}>
                      {truncate(row.english_translation, 60)}
                    </td>

                    {/* Haiku Decision */}
                    <td className="px-2 py-2 text-[11px] text-blue-200" style={{ borderRight:'1px solid #111', background:'#0d1117', maxWidth:180 }} title={row.haiku_intent_summary}>
                      {truncate(row.haiku_intent_summary, 50)}
                    </td>

                    {/* Routing */}
                    <td className="px-2 py-2 text-center" style={{ borderRight:'1px solid #111' }}>
                      <RoutingBadge method={row.routing_method} gated={row.confidence_gate_triggered}/>
                    </td>

                    {/* Agents Considered */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }}>
                      <div className="flex flex-wrap gap-0.5">
                        {(row.agents_considered || []).slice(0,4).map((a,i) => (
                          <AgentBadge key={a.agent_id ?? `agent-pos-${i}`} id={a.agent_id} name={a.agent_name} winner={a.agent_id === row.agent_routed_final}/>
                        ))}
                        {(row.agents_considered || []).length > 4 && <span className="text-gray-500 text-[10px]">+{row.agents_considered.length-4}</span>}
                      </div>
                    </td>

                    {/* Final Agent */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }}>
                      {row.agent_routed_final
                        ? <span style={{ background:cl.bg, color:cl.tx, border:`1px solid ${cl.border}` }}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono">
                            #{row.agent_routed_final} <span className="opacity-80">{row.agent_routed_name?.split(' ').slice(0,2).join(' ')}</span>
                          </span>
                        : <span className="text-gray-600">—</span>
                      }
                    </td>

                    {/* Routing Model */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }} data-testid={`row-routing-model-${idx}`}>
                      <ModelBadge modelId={row.router_model_used} title={`Routing Model: ${row.router_model_used || 'unknown'}`}/>
                    </td>

                    {/* Generation Model */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }} data-testid={`row-generation-model-${idx}`}>
                      <ModelBadge modelId={row.model_used} title={`Generation Model: ${row.model_used || 'unknown'}`}/>
                    </td>

                    {/* Fallback Status */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }} data-testid={`row-fallback-${idx}`}>
                      <FallbackBadge row={row}/>
                    </td>

                    {/* Confidence */}
                    <td className="px-2 py-2" style={{ borderRight:'1px solid #111' }}>
                      <ConfBar val={row.haiku_confidence}/>
                    </td>

                    {/* Haiku Time */}
                    <td className="px-2 py-2 text-center" style={{ borderRight:'1px solid #111' }}>
                      <TCell ms={row.response_time_haiku_ms} t1={500} t2={1000}/>
                    </td>

                    {/* Sonnet Response */}
                    <td className="px-2 py-2 text-[11px]" style={{ borderRight:'1px solid #111', background:'#0a130a', maxWidth:220 }} title={row.sonnet_response}>
                      <span className="text-emerald-300">{truncate(row.sonnet_response, 80)}</span>
                    </td>

                    {/* Sonnet Time */}
                    <td className="px-2 py-2 text-center" style={{ borderRight:'1px solid #111' }}>
                      <TCell ms={row.response_time_sonnet_ms} t1={1000} t2={3000}/>
                    </td>

                    {/* Total Time */}
                    <td className="px-2 py-2 text-center" style={{ borderRight:'1px solid #111' }}>
                      <TCell ms={row.response_time_total_ms} t1={3000} t2={7000}/>
                    </td>

                    {/* Timing Breakdown Columns */}
                    {TIMING_BREAKDOWN_COLS.map(tc => (
                      <td key={tc.key} className="px-1 py-2 text-center" style={{ borderRight:'1px solid #111' }}>
                        <TCell ms={row[tc.field]} t1={tc.t1} t2={tc.t2}/>
                      </td>
                    ))}

                    {/* Entry + Thread icon */}
                    <td className="px-2 py-2 text-[11px] font-mono" style={{ borderRight:'1px solid #111' }}>
                      <div className="flex items-center gap-1.5">
                        {hasError && <span title={row.error_message} className="text-red-400 cursor-help">⚠</span>}
                        {row.bill_number
                          ? <span className="text-yellow-300">{row.bill_number}</span>
                          : row.entry_id
                            ? <span className="text-blue-300">{row.entry_id.slice(0,8)}…</span>
                            : <span className="text-gray-600">—</span>
                        }
                        {/* Thread / Replay button */}
                        <button
                          data-testid={`thread-btn-${idx}`}
                          title="View conversation thread"
                          onClick={e => { e.stopPropagation(); setReplayUser(row); }}
                          className="ml-auto opacity-40 hover:opacity-100 transition-opacity shrink-0"
                          style={{ color: '#60a5fa' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Infinite scroll sentinel */}
        <div ref={bottomRef} className="h-4"/>
        {loadingMore && (
          <div className="text-center py-4 text-gray-500 text-xs">Loading older messages…</div>
        )}
        {!hasMore && rows.length > 0 && (
          <div className="text-center py-4 text-gray-600 text-xs">You've reached the beginning of the log</div>
        )}
      </div>

      {/* ── Row Expansion Modal ─────────────────────────────────────────── */}
      {expandedRow && <ExpandModal row={expandedRow} onClose={() => setExpandedRow(null)}/>}

      {/* ── Session Replay Panel ─────────────────────────────────────────── */}
      {replayUser && (
        <ReplayPanel
          user={replayUser}
          rows={rows}
          onClose={() => setReplayUser(null)}
        />
      )}

      {/* ── Model Settings Panel ──────────────────────────────────────────── */}
      {showSettings && (
        <ModelSettingsPanel onClose={() => setShowSettings(false)}/>
      )}

      {/* ── Hook Messages Settings Panel ──────────────────────────────────── */}
      {showHookPanel && (
        <HookMessagesPanel onClose={() => setShowHookPanel(false)}/>
      )}
    </div>
  );
};

export default ConversationLogPage;
